/* ==========================================================
   J9 Size Finder \u2014 assets/j9-size-finder.js
   - Moves the "Find my size" button next to the theme's size chart link
   - Popup: bust / waist / hip (+ fit preference) \u2192 recommended size
   - Selects that size on the product page
   - Remembers measurements on the device and shows "Your size: M"
   ========================================================== */
(function () {
  'use strict';
  if (window.J9SizeFinder) return;

  var STORE_KEY = 'j9sf:v1';
  var IN_PER_CM = 1 / 2.54;
  var MEASURES = ['bust', 'waist', 'hip'];
  var LABEL = { bust: 'Bust', waist: 'Waist', hip: 'Hip' };

  /* ---------- helpers ---------- */
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function round(v, unit) { return unit === 'cm' ? Math.round(v) : Math.round(v * 2) / 2; }
  function convert(v, from, to) {
    if (!v || from === to) return v;
    return from === 'cm' ? v * IN_PER_CM : v / IN_PER_CM;
  }
  function fmt(v) { return (Math.round(v * 10) / 10).toString(); }

  function readStore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeStore(data) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* private mode etc. */ }
  }

  function visible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return !!(r.width || r.height || el.getClientRects().length) && getComputedStyle(el).visibility !== 'hidden';
  }

  /* ---------- size chart ---------- */
  // Accepts the block array [{size, aliases, bust:[a,b], ...}]
  // or a metafield object {"S": {"bust": [33,34], "waist": [27,28], "hip": [36,37]}, ...}
  function normaliseChart(raw) {
    var list = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === 'object') {
      var src = raw.sizes && typeof raw.sizes === 'object' ? raw.sizes : raw;
      list = Object.keys(src).map(function (k) {
        var o = src[k] || {};
        return Object.assign({ size: k }, o);
      });
    }
    return list.map(function (row) {
      var aliases = row.aliases;
      if (typeof aliases === 'string') aliases = aliases.split(',');
      var r = { size: String(row.size || '').trim(), keys: [], ranges: {} };
      r.keys = [r.size].concat(Array.isArray(aliases) ? aliases : []).map(norm).filter(Boolean);
      MEASURES.forEach(function (m) {
        var v = row[m];
        var lo = 0, hi = 0;
        if (Array.isArray(v)) { lo = num(v[0]); hi = num(v[1] != null ? v[1] : v[0]); }
        else if (v != null) { lo = hi = num(v); }
        if (hi && lo > hi) { var t = lo; lo = hi; hi = t; }
        if (!lo && hi) lo = hi;
        if (hi > 0) r.ranges[m] = [lo, hi];
      });
      return r;
    }).filter(function (r) { return r.size; });
  }

  /* ---------- recommendation ---------- */
  // values: {bust, waist, hip} in chart units. fit: snug | regular | relaxed
  function recommend(chart, values, fit, unit) {
    var tol = unit === 'cm' ? 1.3 : 0.5;            // half-inch grace on each side
    var used = MEASURES.filter(function (m) { return values[m] > 0; });

    function score(size) {
      var pen = 0, poss = [], tight = false, counted = 0;
      used.forEach(function (m) {
        var rg = size.ranges[m];
        if (!rg) return;
        counted++;
        var lo = rg[0] - tol / 2, hi = rg[1] + tol / 2, v = values[m];
        if (v < lo) pen += (lo - v);
        else if (v > hi) { pen += (v - hi) * 1.5; tight = true; }   // too tight is worse than too loose
        poss.push(rg[1] > rg[0] ? Math.max(0, Math.min(1, (v - rg[0]) / (rg[1] - rg[0]))) : 0.5);
      });
      return {
        pen: counted ? pen : Infinity,
        maxPos: poss.length ? Math.max.apply(null, poss) : 0.5,
        minPos: poss.length ? Math.min.apply(null, poss) : 0.5,
        tight: tight
      };
    }

    var scores = chart.map(score);
    var best = -1;
    scores.forEach(function (sc, i) {
      if (sc.pen === Infinity) return;
      if (best < 0) { best = i; return; }
      var b = scores[best];
      if (sc.pen < b.pen - 1e-9) best = i;
      else if (Math.abs(sc.pen - b.pen) < 1e-9 && fit !== 'snug') best = i;   // tie \u2192 larger size (unless snug)
    });
    if (best < 0) return null;

    var pick = best;
    var unitStep = unit === 'cm' ? 2.5 : 1;
    if (fit === 'relaxed' && pick < chart.length - 1 && (scores[pick].maxPos >= 0.6 || scores[pick].tight)) {
      pick++;
    } else if (fit === 'snug' && pick > 0 && !scores[pick].tight && scores[pick - 1].pen <= unitStep && !scores[pick - 1].tight) {
      pick--;
    }

    var s = scores[pick];
    var adjusted = pick !== best;
    var alt = null;
    if ((s.maxPos >= 0.85 || s.tight) && pick < chart.length - 1 && fit !== 'relaxed') alt = { dir: 'up', size: chart[pick + 1].size };
    else if (s.minPos <= 0.15 && pick > 0 && fit === 'regular' && !s.tight && scores[pick - 1].pen <= unitStep) alt = { dir: 'down', size: chart[pick - 1].size };

    var base = adjusted ? scores[best] : s;   // sized up/down on purpose \u2192 judge by the true-fit size
    var level = base.pen === 0 ? 'great' : (base.pen <= unitStep * 1.5 ? 'good' : 'closest');

    var breakdown = used.map(function (m) {
      var rg = chart[pick].ranges[m];
      var v = values[m];
      var state = !rg ? 'na' : (v < rg[0] - tol / 2 ? 'below' : (v > rg[1] + tol / 2 ? 'above' : 'ok'));
      return { m: m, v: v, rg: rg, state: state };
    });

    return { size: chart[pick], level: level, alt: alt, breakdown: breakdown, outOfRange: scores[best].pen > unitStep * 3 };
  }

  /* ---------- product page size selector ---------- */
  function sizeInputs(scope, optionIndex) {
    var roots = [scope, document];
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r];
      if (!root) continue;
      // Impulse / Motion / Streamline: <fieldset data-handle="size">
      var fs = Array.prototype.filter.call(root.querySelectorAll('fieldset[data-handle="size"], fieldset[data-handle="sizes"]'), visible)[0];
      if (fs) return { radios: fs.querySelectorAll('input[type="radio"]'), select: null };
      // Dawn & most themes: radio inputs named "Size" / "options[Size]"
      var radios = Array.prototype.filter.call(root.querySelectorAll('input[type="radio"]'), function (i) {
        return /(^|\[)sizes?(\]|$)/i.test(i.name) || /size/i.test(i.closest('fieldset') && i.closest('fieldset').querySelector('legend') ? i.closest('fieldset').querySelector('legend').textContent : '');
      });
      if (radios.length) return { radios: radios, select: null };
      // Dropdowns
      var sel = Array.prototype.filter.call(root.querySelectorAll('select'), function (s) {
        if (!visible(s) && !s.closest('[data-index]')) return false;
        return /(^|\[)sizes?(\]|$)/i.test(s.name) || /size/i.test(s.id) || (optionIndex >= 0 && s.getAttribute('data-index') === 'option' + (optionIndex + 1));
      })[0];
      if (sel) return { radios: [], select: sel };
    }
    return null;
  }

  function selectSize(sizeRow, scope, optionIndex) {
    var found = sizeInputs(scope, optionIndex);
    if (!found) return false;
    var keys = sizeRow.keys;
    if (found.select) {
      var opt = Array.prototype.find.call(found.select.options, function (o) { return keys.indexOf(norm(o.value)) > -1 || keys.indexOf(norm(o.textContent)) > -1; });
      if (!opt) return false;
      found.select.value = opt.value;
      found.select.dispatchEvent(new Event('change', { bubbles: true }));
      return found.select;
    }
    var radio = Array.prototype.find.call(found.radios, function (i) { return keys.indexOf(norm(i.value)) > -1; });
    if (!radio) return false;
    if (!radio.checked) radio.click();
    if (!radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    return radio;
  }

  /* ---------- placement next to the size chart link ---------- */
  function findTarget(selectorList, root) {
    var sels = String(selectorList || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    for (var i = 0; i < sels.length; i++) {
      var els;
      try { els = document.querySelectorAll(sels[i]); } catch (e) { continue; }
      for (var j = 0; j < els.length; j++) {
        var el = els[j];
        if (root.contains(el) || el.closest('.j9sf-dialog, [role="dialog"], dialog, .modal, .drawer')) continue;
        if (visible(el)) return el;
      }
    }
    return null;
  }

  function findSizeLabel() {
    var fs = Array.prototype.filter.call(document.querySelectorAll('fieldset[data-handle="size"], fieldset[data-handle="sizes"]'), visible)[0];
    if (fs) {
      var wrap = fs.closest('.variant-wrapper') || fs.parentElement;
      var lab = wrap && (wrap.querySelector('.variant__label') || wrap.querySelector('label, legend'));
      if (lab && visible(lab)) return lab;
      var lg = fs.querySelector('legend');
      if (lg) return lg;
    }
    var cand = Array.prototype.filter.call(document.querySelectorAll('legend, label'), function (el) {
      return visible(el) && /^\s*sizes?\b/i.test(el.textContent) && !el.closest('.j9sf-dialog, dialog, .drawer, footer, header');
    });
    return cand[0] || null;
  }

  /* ---------- one instance per section ---------- */
  function SizeFinder(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    var ds = root.dataset;
    this.chartUnit = ds.chartUnit === 'cm' ? 'cm' : 'in';
    this.unit = ds.defaultUnit === 'cm' ? 'cm' : 'in';
    this.optionIndex = parseInt(ds.sizeOption, 10);
    this.remember = ds.remember === 'true';

    try { this.chart = normaliseChart(JSON.parse(root.querySelector('[data-j9sf-chart]').textContent)); } catch (e) { this.chart = []; }
    try {
      var stock = JSON.parse(root.querySelector('[data-j9sf-stock]').textContent);
      this.stock = {};
      stock.forEach(function (s) { var k = norm(s.v); this.stock[k] = this.stock[k] || !!s.a; }, this);
      this.hasStock = stock.length > 0;
    } catch (e) { this.stock = {}; this.hasStock = false; }

    this.used = MEASURES.filter(function (m) { return this.chart.some(function (r) { return r.ranges[m]; }); }, this);

    this.btn = root.querySelector('[data-j9sf-open]');
    this.dialog = root.querySelector('[data-j9sf-dialog]');
    if (!this.btn || !this.dialog || !this.chart.length || !this.used.length) {
      if (this.btn) this.btn.hidden = true;
      return;
    }

    this.cleanupOld();
    document.body.appendChild(this.dialog);   // so it opens even if the section wrapper is hidden
    this.place();
    this.bind();
    this.showSaved();
  }

  SizeFinder.prototype.cleanupOld = function () {
    var id = this.id, root = this.root, dialog = this.dialog, btn = this.btn;
    document.querySelectorAll('.j9sf-btn[data-section-id="' + id + '"], .j9sf-dialog[data-section-id="' + id + '"]').forEach(function (el) {
      if (el !== btn && el !== dialog && !root.contains(el)) el.remove();
    });
  };

  // Elements that are the actual clickable "Size chart" link (inline), checked first
  var INLINE_TRIGGERS = 'tool-tip-trigger[data-tool-tip="size-chart"], [data-tool-tip="size-chart"], [data-tool-tip="SizeChart"], .variant__size-chart';

  SizeFinder.prototype.place = function () {
    var ds = this.root.dataset;
    var target = findTarget(INLINE_TRIGGERS, this.root) || findTarget(ds.target, this.root);
    var before = ds.position === 'before';
    // If a full-width wrapper was found (e.g. Impulse .size-chart__standalone), use the link inside it
    if (target && !target.matches(INLINE_TRIGGERS)) {
      var inner = target.querySelector(INLINE_TRIGGERS + ', a, button');
      if (inner && visible(inner)) target = inner;
    }
    var labelFallback = false;
    if (!target) {
      target = findSizeLabel();
      before = false;
      labelFallback = true;
    }
    if (target) {
      this.btn.hidden = false;
      this.btn.classList.add('is-placed');
      this.btn.classList.toggle('is-before', before);

      if (labelFallback) {
        target.insertAdjacentElement('afterend', this.btn);
      } else {
        // Find the whole size-chart row (the theme block that holds the link)
        var row = target.closest('.size-chart__standalone');
        if (!row) {
          var blk = target.closest('.product-block, [class*="product-block"], [data-block-type]');
          if (blk && !blk.querySelector('fieldset, select, form, input')) row = blk;
        }
        if (!row) row = target.parentElement;
        var layout = ds.layout || 'spread';
        row.classList.add('j9sf-row', 'j9sf-row--' + layout, 'j9sf-row--m-' + (ds.layoutMobile || 'center'));
        if (before) row.insertBefore(this.btn, row.firstChild);
        else row.appendChild(this.btn);
      }
      this.scope = target.closest('.shopify-section, [data-section-type], form, main') || document;

      // The section itself is now empty — make sure it takes up no space on the page
      var host = this.root.closest('.shopify-section');
      if (host && !host.contains(this.btn)) host.classList.add('j9sf-host-empty');
    } else if (ds.showFallback !== 'true') {
      this.btn.hidden = true;
    }
  };

  SizeFinder.prototype.bind = function () {
    var self = this;
    var d = this.dialog;
    this.form = d.querySelector('[data-j9sf-form]');
    this.resultEl = d.querySelector('[data-j9sf-result]');
    this.errorEl = d.querySelector('[data-j9sf-error]');

    // hide measurements that this size chart doesn't use
    MEASURES.forEach(function (m) {
      var f = d.querySelector('[data-measure="' + m + '"]');
      if (f) f.hidden = self.used.indexOf(m) < 0;
    });
    d.querySelectorAll('[data-measure] input').forEach(function (i) { i.dataset.ph = i.placeholder; });

    this.btn.addEventListener('click', function () { self.open(); });
    d.querySelector('[data-j9sf-close]').addEventListener('click', function () { self.close(); });
    d.addEventListener('click', function (e) { if (e.target === d) self.close(); });   // backdrop
    d.addEventListener('close', function () { document.documentElement.classList.remove('j9sf-lock'); });

    this.form.addEventListener('change', function (e) {
      if (e.target.name === 'unit') self.setUnit(e.target.value, true);
    });
    this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(); });
    d.querySelector('[data-j9sf-restart]').addEventListener('click', function () { self.showForm(); });
    d.querySelector('[data-j9sf-select]').addEventListener('click', function () { self.applySize(true); });

    this.setUnit(this.unit, false);
  };

  SizeFinder.prototype.setUnit = function (unit, convertValues) {
    var prev = this.unit;
    this.unit = unit === 'cm' ? 'cm' : 'in';
    var self = this;
    this.form.querySelectorAll('input[name="unit"]').forEach(function (r) { r.checked = r.value === self.unit; });
    this.form.querySelectorAll('[data-j9sf-unit-label]').forEach(function (s) { s.textContent = self.unit; });
    this.form.querySelectorAll('[data-measure] input').forEach(function (i) {
      if (convertValues && i.value && prev !== self.unit) i.value = round(convert(num(i.value), prev, self.unit), self.unit);
      i.step = self.unit === 'cm' ? '1' : '0.5';
      var ph = i.dataset.ph || '';
      i.placeholder = self.unit === 'in' ? ph : ph.replace(/\d+(\.\d+)?/g, function (n) { return Math.round(num(n) * 2.54); });
    });
  };

  SizeFinder.prototype.open = function () {
    var saved = this.remember ? readStore() : null;
    if (saved && saved.m) {
      this.setUnit(saved.unit || this.unit, false);
      var self = this;
      MEASURES.forEach(function (m) {
        var i = self.form.querySelector('input[name="' + m + '"]');
        if (i && saved.m[m]) i.value = round(convert(saved.m[m], 'in', self.unit), self.unit);
      });
      var fitInput = this.form.querySelector('input[name="fit"][value="' + saved.fit + '"]');
      if (fitInput) fitInput.checked = true;
    }
    this.showForm();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    document.documentElement.classList.add('j9sf-lock');
  };

  SizeFinder.prototype.close = function () {
    if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
    else this.dialog.removeAttribute('open');
    document.documentElement.classList.remove('j9sf-lock');
    this.btn.focus({ preventScroll: true });
  };

  SizeFinder.prototype.showForm = function () {
    this.form.hidden = false;
    this.resultEl.hidden = true;
    this.errorEl.hidden = true;
  };

  SizeFinder.prototype.readValues = function () {
    var self = this, vals = {}, errors = [];
    var min = this.unit === 'cm' ? 50 : 20, max = this.unit === 'cm' ? 180 : 70;
    this.used.forEach(function (m) {
      var i = self.form.querySelector('input[name="' + m + '"]');
      var v = num(i && i.value);
      i && i.setAttribute('aria-invalid', 'false');
      if (!v) return;
      if (v < min || v > max) { errors.push(LABEL[m]); i.setAttribute('aria-invalid', 'true'); return; }
      vals[m] = v;
    });
    return { vals: vals, errors: errors };
  };

  SizeFinder.prototype.submit = function () {
    var r = this.readValues();
    var unitName = this.unit === 'cm' ? 'centimetres' : 'inches';
    if (r.errors.length) return this.error('Please check ' + r.errors.join(', ') + ' \u2014 it should be in ' + unitName + '.');
    if (!Object.keys(r.vals).length) return this.error('Please enter at least one measurement.');

    var fitEl = this.form.querySelector('input[name="fit"]:checked');
    var fit = fitEl ? fitEl.value : 'regular';

    var inChart = {}, inInches = {}, self = this;
    Object.keys(r.vals).forEach(function (m) {
      inChart[m] = convert(r.vals[m], self.unit, self.chartUnit);
      inInches[m] = convert(r.vals[m], self.unit, 'in');
    });

    var rec = recommend(this.chart, inChart, fit, this.chartUnit);
    if (!rec) return this.error('Sorry, we couldn\'t match these measurements. Please check the size chart.');

    if (this.remember) writeStore({ m: inInches, unit: this.unit, fit: fit, t: Date.now() });
    this.rec = rec;
    this.renderResult(rec);
    this.showSaved();
  };

  SizeFinder.prototype.error = function (msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  };

  SizeFinder.prototype.inStock = function (row) {
    if (!this.hasStock) return true;
    var self = this;
    var known = row.keys.filter(function (k) { return k in self.stock; });
    if (!known.length) return true;            // size not sold in this product \u2192 don't warn
    return known.some(function (k) { return self.stock[k]; });
  };

  SizeFinder.prototype.renderResult = function (rec) {
    var d = this.dialog, self = this;
    d.querySelector('[data-j9sf-size]').textContent = rec.size.size;

    var conf = d.querySelector('[data-j9sf-confidence]');
    conf.className = 'j9sf-result__fit is-' + rec.level;
    conf.textContent = rec.outOfRange
      ? 'Your measurements are outside our size range \u2014 this is the closest size.'
      : (rec.level === 'great' ? 'Great fit' : rec.level === 'good' ? 'Good fit' : 'Closest fit \u2014 please check the size chart');

    var list = d.querySelector('[data-j9sf-breakdown]');
    list.textContent = '';
    rec.breakdown.forEach(function (b) {
      var li = document.createElement('li');
      var unit = self.unit;
      var shown = round(convert(b.v, self.chartUnit, unit), unit);
      var left = document.createElement('span');
      left.innerHTML = '<b></b> ';
      left.querySelector('b').textContent = LABEL[b.m];
      left.appendChild(document.createTextNode(fmt(shown) + ' ' + unit));
      var right = document.createElement('span');
      if (b.rg) {
        var lo = round(convert(b.rg[0], self.chartUnit, unit), unit), hi = round(convert(b.rg[1], self.chartUnit, unit), unit);
        var rangeTxt = lo === hi ? fmt(lo) : fmt(lo) + '\u2013' + fmt(hi);
        right.textContent = b.state === 'ok' ? '\u2713 fits ' + rec.size.size + ' (' + rangeTxt + ')'
          : (b.state === 'above' ? 'a little snug (' + rangeTxt + ')' : 'a little loose (' + rangeTxt + ')');
        right.className = b.state === 'ok' ? 'is-ok' : 'is-off';
      } else {
        right.textContent = '\u2014';
      }
      li.appendChild(left);
      li.appendChild(right);
      list.appendChild(li);
    });

    var alt = d.querySelector('[data-j9sf-alt]');
    if (rec.alt) {
      alt.textContent = rec.alt.dir === 'up'
        ? 'Between sizes? Choose ' + rec.alt.size + ' for a more relaxed fit.'
        : 'Prefer a closer fit? ' + rec.alt.size + ' may also work.';
      alt.hidden = false;
    } else {
      alt.hidden = true;
    }

    var oos = !this.inStock(rec.size);
    d.querySelector('[data-j9sf-stock-note]').hidden = !oos;

    var sel = d.querySelector('[data-j9sf-select]');
    sel.textContent = (sel.dataset.label || 'Select size [size]').replace(/\[size\]/g, rec.size.size);
    var canSelect = !!sizeInputs(this.scope || document, this.optionIndex) && !oos;
    sel.hidden = !canSelect;

    this.form.hidden = true;
    this.resultEl.hidden = false;
    this.resultEl.scrollIntoView({ block: 'nearest' });

    if (this.root.dataset.autoSelect === 'true' && canSelect) this.applySize(false);
  };

  SizeFinder.prototype.applySize = function (closeAfter) {
    if (!this.rec) return;
    var el = selectSize(this.rec.size, this.scope || document, this.optionIndex);
    if (!el) return;
    if (closeAfter) {
      this.close();
      var label = (el.id && document.querySelector('label[for="' + CSS.escape(el.id) + '"]')) || el;
      label.scrollIntoView({ behavior: 'smooth', block: 'center' });
      label.classList.add('j9sf-flash');
      setTimeout(function () { label.classList.remove('j9sf-flash'); }, 1600);
    }
    document.dispatchEvent(new CustomEvent('j9sf:size-selected', { detail: { size: this.rec.size.size } }));
  };

  SizeFinder.prototype.showSaved = function () {
    if (this.root.dataset.showSaved !== 'true' || !this.remember) return;
    var saved = readStore();
    if (!saved || !saved.m) return;
    var vals = {}, self = this;
    Object.keys(saved.m).forEach(function (m) {
      if (self.used.indexOf(m) > -1 && saved.m[m]) vals[m] = convert(saved.m[m], 'in', self.chartUnit);
    });
    if (!Object.keys(vals).length) return;
    var rec = recommend(this.chart, vals, saved.fit || 'regular', this.chartUnit);
    if (!rec) return;
    var txt = this.btn.querySelector('[data-j9sf-btn-text]');
    if (!txt.dataset.orig) txt.dataset.orig = txt.textContent;
    txt.textContent = (this.root.dataset.savedLabel || 'Your size: [size]').replace(/\[size\]/g, rec.size.size);
    this.btn.classList.add('has-saved');
  };

  /* ---------- boot ---------- */
  function initAll(scope) {
    (scope || document).querySelectorAll('[data-j9sf-root]').forEach(function (root) {
      if (root._j9sf) return;
      root._j9sf = new SizeFinder(root);
    });
  }

  function boot() { initAll(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Some themes render the variant picker late \u2014 try again once everything has loaded
  window.addEventListener('load', function () {
    document.querySelectorAll('[data-j9sf-root]').forEach(function (root) {
      var inst = root._j9sf;
      if (inst && inst.btn && !inst.btn.classList.contains('is-placed') && inst.dialog && inst.form) inst.place();
    });
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-j9sf-root]');
    if (!root) return;
    document.querySelectorAll('.j9sf-btn[data-section-id="' + root.dataset.sectionId + '"], .j9sf-dialog[data-section-id="' + root.dataset.sectionId + '"]').forEach(function (el) { el.remove(); });
  });

  // Exposed for debugging in the console
  window.J9SizeFinder = { recommend: recommend, normaliseChart: normaliseChart, init: initAll };
})();